import { useTranslation } from 'react-i18next';
import { ChevronRight, Star, Type, Upload } from 'lucide-react';
import type { DirectSaleTool } from './DirectSaleDesignToolbar';
import { DirectSaleSheet } from './DirectSaleSheet';

/**
 * The phone's answer to a tap on the product: what should go there?
 *
 * A customer who taps the shirt has said where they want something, not
 * what. The three ways in are the same as the bottom card's tiles and lead
 * into the same flows — this only meets the customer where their finger is.
 *
 * `onPick` runs synchronously inside the tap: the upload opens the system
 * picker and the text opens the keyboard, and iOS allows both only within
 * the user gesture.
 */
export function DirectSaleAddModal({
  onPick,
  onClose,
}: {
  onPick: (tool: DirectSaleTool) => void;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const choices: {
    tool: DirectSaleTool;
    icon: typeof Upload;
    label: string;
    hint: string;
  }[] = [
    {
      tool: 'upload',
      icon: Upload,
      label: t('directSale.mobile.addUpload'),
      hint: t('directSale.mobile.addUploadHint'),
    },
    {
      tool: 'motifs',
      icon: Star,
      label: t('directSale.mobile.addMotif'),
      hint: t('directSale.mobile.addMotifHint'),
    },
    {
      tool: 'text',
      icon: Type,
      label: t('directSale.mobile.addText'),
      hint: t('directSale.mobile.addTextHint'),
    },
  ];

  return (
    <DirectSaleSheet
      title={t('directSale.mobile.addTitle')}
      modal
      onClose={onClose}
    >
      <div className="flex flex-col gap-2 px-3 pb-2 pt-2">
        {choices.map(({ tool, icon: Icon, label, hint }) => (
          <button
            key={tool}
            type="button"
            onClick={() => onPick(tool)}
            className="flex h-[68px] items-center gap-3.5 rounded-card bg-paper-2 px-4 text-left"
          >
            <span className="flex h-11 w-11 flex-none items-center justify-center rounded-full bg-paper text-primary-700">
              <Icon className="h-[22px] w-[22px]" />
            </span>
            <span className="min-w-0 flex-1">
              <span className="block text-[15px] font-bold text-ink">
                {label}
              </span>
              <span className="block truncate text-[13px] text-ink-3">
                {hint}
              </span>
            </span>
            <ChevronRight className="h-5 w-5 flex-none text-ink-3" />
          </button>
        ))}
      </div>
    </DirectSaleSheet>
  );
}
